import React, { useContext, useEffect, useState } from "react";
import {
  getParameterStore,
  putParameterStore,
} from "../../apiClient/operations/ssmOperations";
import Table from "../../tailwindUI/Table";
import useFormatDate from "../../hooks/useFormatDate";
import { ModalNotification } from "../modals/ModalNotification";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import PrimaryButton from "../../tailwindUI/PrimaryButton";
import { AgentPaymentLinkSlideForm } from "./AgentPaymentLinkSlideForm";
import { AlertContext } from "../../context/AlertContext";
import EmptyState from "../../tailwindUI/EmptyState";

const columns = [
  { heading: "Nombre", value: "nameId", main: true },
  { heading: "Fecha inicial", value: "from_date" },
  { heading: "Fecha final", value: "to_date" },
  { heading: "Fecha de expiración", value: "expires_at" },
  { heading: "Estatus", value: "status", badge: true },
];

export function Agent() {
  const [ulinksParms, setUlinksParms] = useState([]);
  const [linkToUpdate, setLinkToUpdate] = useState(null);
  const [linkToDelete, setLinkToDelete] = useState(null);
  const [currentLink, setCurrentLink] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [openSlideForm, setOpenSlideForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const { setAlert } = useContext(AlertContext);
  const { formatDate } = useFormatDate();

  const columnActions = [
    {
      id: 1,
      name: "Eliminar",
      type: "primary",
      icon: <TrashIcon className="w-5 h-5 text-gray-600 lg:text-white" />,
      action: (item) => {
        setShowDeleteModal(true);
        setLinkToDelete(item);
      },
    },
    {
      id: 2,
      name: "Editar",
      type: "secondary",
      icon: <PencilSquareIcon className="w-5 h-5 text-gray-600" />,
      action: (item) => {
        setOpenSlideForm(true);
        const linkToUpdate = ulinksParms.find((link) => link.id === item.id);
        setLinkToUpdate(linkToUpdate);
      },
    },
  ];

  const handleDeleteLink = async () => {
    try {
      setIsPending(true);
      const linksFiltered = ulinksParms.filter(
        (link) => link.id !== linkToDelete.id
      );
      await putParameterStore("fxt-api-payment-ulinks-settings", {
        universal_payment_links: linksFiltered,
      });
      setUlinksParms(linksFiltered);
      setAlert({
        active: true,
        type: "success",
        message: "Link de pago eliminado correctamente",
      });
    } catch (error) {
      setAlert({
        active: true,
        type: "error",
        message: error?.message || "Error al procesar tu solicitud",
      });
    } finally {
      setShowDeleteModal(false);
      setLinkToDelete(null);
      setIsPending(false);
    }
  };

  const getTransformedData = (ulinks) => {
    if (!ulinks) return [];
    return ulinks.map((link) => ({
      id: link.id,
      name: link.name,
      nameId: (
        <div className="flex flex-col">
          <p className="text-gray-900 text-sm font-medium">{link.name}</p>
          <p className="text-gray-500 text-sm font-normal">{link.id}</p>
        </div>
      ),
      from_date: formatDate(link.from_date, "DD MMM YYYY, hh:mm:ss A"),
      to_date: formatDate(link.to_date, "DD MMM YYYY, hh:mm:ss A"),
      expires_at: formatDate(link.expires_at, "DD MMM YYYY, hh:mm:ss A"),
      status:
        link.id === currentLink?.id
          ? "current_v2"
          : link.to_date < new Date().getTime()
          ? "completed_v2"
          : link.expires_at < new Date().getTime()
          ? "expired"
          : "pending",
    }));
  };

  useEffect(() => {
    const getPaymentSettings = async () => {
      try {
        setIsLoading(true);
        const response = await getParameterStore(
          "fxt-api-payment-ulinks-settings"
        );
        if (response) {
          setUlinksParms(response?.universal_payment_links || []);
        }
      } catch (error) {
        console.error("Error fetching payment settings:", error);
      } finally {
        setIsLoading(false);
      }
    };
    getPaymentSettings();
  }, []);

  useEffect(() => {
    if (ulinksParms.length > 0) {
      const currentLink = ulinksParms.find(
        (link) =>
          link.to_date > new Date().getTime() &&
          link.expires_at > new Date().getTime()
      );
      setCurrentLink(currentLink);
    }
  }, [ulinksParms]);

  return (
    <>
      <div className="w-full h-full">
        <div className="w-full xs:h-full md:h-full">
          <div className="mt-10 md:mt-0 w-full py-5  xs:h-auto md:h-1/12">
            <div className="flex justify-between items-center w-full">
              <h1 className="hidden md:flex text-4xl font-bold text-left">
                Links universales
              </h1>
              <PrimaryButton
                type="button"
                onClick={() => {
                  setLinkToUpdate(null);
                  setOpenSlideForm(true);
                }}
                disabled={isLoading}
              >
                Agregar link universal
              </PrimaryButton>
            </div>
          </div>
          {ulinksParms.length > 0 || isLoading ? (
            <Table
              title="Links universales"
              columns={columns}
              data={getTransformedData(ulinksParms)}
              actions={columnActions}
              isLoadingData={isLoading}
            />
          ) : (
            <EmptyState
              title="No se encontraron links universales"
              text="Agrega un nuevo link para comenzar"
            />
          )}
        </div>
      </div>

      <AgentPaymentLinkSlideForm
        linksParams={ulinksParms}
        currentLink={currentLink}
        setLinksParams={setUlinksParms}
        linkToUpdate={linkToUpdate}
        openSlideForm={openSlideForm}
        setOpenSlideForm={setOpenSlideForm}
      />

      <ModalNotification
        type="confirm"
        title="¿Estás seguro de que deseas eliminar el link universal?"
        onConfirm={handleDeleteLink}
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        isLoading={isPending}
        classNameActions="flex-row justify-end w-full max-w-[200px] ml-auto"
      />
    </>
  );
}
